import { IColumn, IDragDropContext, IDragDropEvents, ISearchBox, mergeStyles } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPCheckbox, IPScrollablePane, IPSearchBox, IPText } from 'baseComponents';
import { IPDetailsList } from 'baseComponents/DetailsList/IPDetailsList';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectionMode } from 'baseComponents/Utils/Selection';
import { LearnMore } from 'components/Notification/Info';
import { globalFilterDelay, globalKB_howToWorkWithFormsChecklist } from 'globalConstants';
import {
  getGlobalStackItemStylesPaddingSceneScrollMinHeight,
  globalSearchBoxStyles,
  globalStackItemStylesPaddingScene,
  globalStackTokensGapLarge,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
} from 'globalStyles';
import { TaskCheckList, TaskCheckListItem, TaskCheckListItemState } from 'models/tasks/taskHelperClasses';
import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import { htmlToText } from 'utils/html';
import { getRandomId } from 'utils/string';
import { AddNewCheckListItem, IAddNewCheckListItemHandle } from './AddNewCheckListItem';
import { CheckListItemRow } from './CheckListItemRow';

interface ICheckList {
  allowEdit: boolean;
  allowFill: boolean;
  allowComment: boolean;
  singleLineEdit?: boolean;
  compact?: boolean;
  checkList?: TaskCheckList;
  updateCheckList: (newCheckList: TaskCheckList) => void;
  // 'page' is the task detail view: filter bar (search + open/failed toggles), keyboard shortcuts
  // ('/' focuses the filter, 'n' the new item editor), a pasted list can be added as separate
  // items, the new item editor and its Add button on one row, and after an add the list scrolls to
  // the new item and the editor is focused. Left out, the checklist behaves as it always did.
  variant?: 'default' | 'page';
  onPendingNewItemChange?: (hasPendingText: boolean) => void; // pending text in the add row
  hideEmptyState?: boolean; // no empty state text or form hint while the list has no items
}

const getPlainText = (html: string | undefined): string => htmlToText(html, false).toLowerCase();

const isTypingTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;

  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
};

export const getRandomIds = (count: number | undefined): string[] => {
  return Array.from({ length: count ?? 0 }, () => 'rsb' + getRandomId());
};

const CheckList = (props: ICheckList) => {
  const { t } = useTranslation(['task', 'translation']);
  const appContext = useContext(AppContext);
  const isPage = props.variant === 'page';
  const [showIcon, setShowIcon] = useState<number | undefined>(undefined);
  const [_draggedItem, _setDraggedItem] = useState<TaskCheckListItem | undefined>(undefined);
  const [items, setItems] = useState<TaskCheckListItem[]>(props.checkList?.items ? props.checkList?.items : []);
  const [isCallOutOpen, setIsCallOutOpen] = useState<TaskCheckListItem | undefined>(undefined);
  const [isEditCallOutOpen, setIsEditCallOutOpen] = useState<TaskCheckListItem | undefined>(undefined);
  const [statusButtonIds, setStatusButtonIds] = useState<string[]>(getRandomIds(props.checkList?.items.length));
  const [newCheckListDescription, setNewCheckListDescription] = useState<string | undefined>(undefined);
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [showOpen, setShowOpen] = useState<boolean>(false);
  const [showFailed, setShowFailed] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const searchBoxRef = useRef<ISearchBox>(null);
  const addItemRef = useRef<IAddNewCheckListItemHandle>(null);
  const rootId = useRef(`checklist-root-${getRandomId()}`);
  //armed with the item count when the user added items: after the render that holds them, scroll to
  //the end and refocus the editor. Only for the page variant.
  const scrollToEndRef = useRef<number | undefined>(undefined);

  const filterActive = isPage && (searchText !== '' || showOpen || showSuccess || showFailed);

  useEffect(() => {
    setStatusButtonIds(getRandomIds(props.checkList?.items.length));
  }, [props.checkList]);

  useEffect(() => {
    addNewCheckListItem(newCheckListDescription);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newCheckListDescription]);

  //the API rejects a checklist that serializes to more than TaskCheckList.maxLength characters.
  //warn when an edit crosses that limit instead of failing on save. an already too long checklist
  //is not blocked so that it can still be made smaller.
  const updateCheckList = (newCheckList: TaskCheckList): boolean => {
    if (newCheckList.exceedsMaxLength() && !props.checkList?.exceedsMaxLength()) {
      appContext.showNotification(
        t('translation:General.Text.MaxLengthExceeded', { max: TaskCheckList.maxLength }),
        true,
        false,
        true,
      );

      return false;
    }

    props.updateCheckList(newCheckList);

    return true;
  };

  const clearSearchTimer = () => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = undefined;
    }
  };

  const clearFilters = () => {
    clearSearchTimer();
    setSearchInput('');
    setSearchText('');
    setShowOpen(false);
    setShowFailed(false);
    setShowSuccess(false);
  };

  useEffect(() => clearSearchTimer, []);

  //after an add: show the new item(s) and keep the user in the editor for the next one
  const afterAdd = () => {
    if (isPage) scrollToEndRef.current = items.length;
    if (isPage) clearFilters();
  };

  useLayoutEffect(() => {
    if (scrollToEndRef.current === undefined) return;

    //the add did not lead to a new item (yet): drop the flag instead of acting on a later,
    //unrelated change
    const armedLength = scrollToEndRef.current;
    scrollToEndRef.current = undefined;
    if (items.length <= armedLength) return;

    const container = document
      .getElementById(rootId.current)
      ?.querySelector<HTMLElement>('.ms-ScrollablePane--contentContainer');
    if (container) container.scrollTop = container.scrollHeight;
    //in compact mode the editor lives in a callout that is closed after an add
    if (!props.compact) addItemRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(() => {
    if (!isPage) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.altKey || e.metaKey || isTypingTarget(e.target)) return;
      //only act when the focus is on the page or inside the modal/layer of this checklist, not in
      //another dialog
      const root = document.getElementById(rootId.current);
      const active = document.activeElement;
      const layer = root?.closest('.ms-Modal, .ms-Layer');
      if (!root || (active !== document.body && layer && !layer.contains(active))) return;

      if (e.key === '/' && isPage) {
        e.preventDefault();
        searchBoxRef.current?.focus();
      } else if (e.key === 'n' && props.allowEdit) {
        e.preventDefault();
        addItemRef.current?.focus();
      }
    };

    //one listener on the document also sees the events of the checklist itself, which bubble up to
    //it
    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isPage, props.allowEdit]);

  const onDelete = (item: TaskCheckListItem) => {
    if (!props.checkList) return;

    const _newCheckList = props.checkList.clone();
    _newCheckList.items = _newCheckList.items.filter((itm) => {
      return itm.id !== item.id;
    });

    props.updateCheckList(_newCheckList);
  };

  const addNewCheckListItem = (value: string | undefined) => {
    if (!value || !props.checkList) return;

    const _newCheckList = props.checkList.clone();
    const _taskCheckListItem = new TaskCheckListItem();
    _taskCheckListItem.description = value;
    _taskCheckListItem.state = TaskCheckListItemState.ToDo;
    _newCheckList.items.push(_taskCheckListItem);
    if (updateCheckList(_newCheckList)) afterAdd();
  };

  const addNewCheckListItems = (values: string[]) => {
    if (values.length === 0 || !props.checkList) return;

    const _newCheckList = props.checkList.clone();
    values.forEach((value) => {
      const _taskCheckListItem = new TaskCheckListItem();
      _taskCheckListItem.description = value;
      _taskCheckListItem.state = TaskCheckListItemState.ToDo;
      _newCheckList.items.push(_taskCheckListItem);
    });
    if (updateCheckList(_newCheckList)) afterAdd();
  };

  const onAddNewCheckListItem = (value: string | undefined) => {
    setNewCheckListDescription(value);
  };

  const updateState = (item: TaskCheckListItem, state: TaskCheckListItemState) => {
    if (!props.checkList) return;

    const _newCheckList = props.checkList.clone();
    _newCheckList.items = _newCheckList.items.map((_itm) => {
      if (_itm.id === item.id) {
        _itm.state = state;

        return _itm;
      } else return _itm;
    });
    props.updateCheckList(_newCheckList);
  };

  const updateDescription = (item: TaskCheckListItem, description: string) => {
    if (!props.checkList) return;
    const _newCheckList = props.checkList.clone();
    _newCheckList.items = _newCheckList.items.map((_itm) => {
      if (_itm.id === item.id) {
        _itm.description = description;

        return _itm;
      } else return _itm;
    });
    updateCheckList(_newCheckList);
  };

  const updateComment = (item: TaskCheckListItem, comment: string | undefined) => {
    if (!props.checkList) return;

    const _newCheckList = props.checkList.clone();
    _newCheckList.items = _newCheckList.items.map((_itm) => {
      if (_itm.id === item.id) {
        _itm.comment = comment;

        return _itm;
      } else return _itm;
    });
    updateCheckList(_newCheckList);
  };

  useEffect(() => {
    setItems(props.checkList?.items ? (props.checkList?.items ? props.checkList?.items : []) : []);
  }, [props.checkList]);

  const matchesFilter = (item: TaskCheckListItem): boolean => {
    if (showOpen || showSuccess || showFailed) {
      const stateMatch =
        (showOpen && item.state === TaskCheckListItemState.ToDo) ||
        (showSuccess && item.state === TaskCheckListItemState.Success) ||
        (showFailed && item.state === TaskCheckListItemState.Failed);
      if (!stateMatch) return false;
    }
    if (searchText) {
      const needle = searchText.toLowerCase();

      return getPlainText(item.description).includes(needle) || getPlainText(item.comment).includes(needle);
    }

    return true;
  };

  const visibleItems = filterActive ? items.filter(matchesFilter) : items;

  const countByState = (state: TaskCheckListItemState): number => items.filter((item) => item.state === state).length;

  const columns: IColumn[] = [
    {
      key: 'text',
      name: '',
      minWidth: 50,
      isMultiline: true,
      onRender: (item?: TaskCheckListItem, index?: number, column?: IColumn) => {
        if (!item || index === undefined) return;

        //the ids and the callouts are keyed on the position in the whole list, also while it is
        //filtered
        const itemIndex = filterActive ? items.indexOf(item) : index;

        return (
          <CheckListItemRow
            allowEdit={props.allowEdit}
            allowFill={props.allowFill}
            allowComment={props.allowComment}
            index={itemIndex}
            setShowIcon={setShowIcon}
            statusButtonId={statusButtonIds[itemIndex]}
            showIcon={showIcon}
            isCallOutOpen={isCallOutOpen}
            setIsCallOutOpen={setIsCallOutOpen}
            isEditCallOutOpen={isEditCallOutOpen}
            setIsEditCallOutOpen={setIsEditCallOutOpen}
            item={item}
            onComment={updateComment}
            onDelete={onDelete}
            onUpdateState={updateState}
            onUpdateDescription={updateDescription}
          />
        );
      },
    },
  ];

  const _insertBeforeItem = (item?: TaskCheckListItem) => {
    if (!item || !_draggedItem) return;

    const insertIndex = items.indexOf(item);
    let newItems = items.map((i) => i.clone());

    newItems = items.filter((itm) => {
      return itm.id !== _draggedItem.id;
    });

    newItems.splice(insertIndex, 0, _draggedItem);

    setItems(newItems);

    const _newCheckList = new TaskCheckList();
    _newCheckList.items = newItems;

    props.updateCheckList(_newCheckList);
  };

  const dragAndDropEventHandler: IDragDropEvents = {
    canDrop: (dropContext?: IDragDropContext, dragContext?: IDragDropContext) => {
      return true;
    },
    canDrag: (item?: TaskCheckListItem) => {
      if (props.allowEdit) return true;

      return false;
    },
    onDragEnter: (item?: TaskCheckListItem, event?: DragEvent) => {
      if (!_draggedItem || !item) return '';
      const sourceIdx = items.indexOf(_draggedItem);
      const targetIdx = items.indexOf(item);

      if (sourceIdx > targetIdx) {
        // return string is the css classes that will be added to the entering element.
        return mergeStyles({
          border: '1px solid blue',
          borderTop: '1px solid blue',
          borderColor: 'blue',
        });
      } else if (sourceIdx < targetIdx) {
        // return string is the css classes that will be added to the entering element.
        return mergeStyles({
          border: '1px solid blue',
          borderBottom: '1px solid blue',
          borderColor: 'blue',
        });
      } else {
        return '';
      }
    },
    onDragLeave: (item?: TaskCheckListItem, event?: DragEvent) => {
      return;
    },
    onDrop: (item?: TaskCheckListItem, event?: DragEvent) => {
      if (_draggedItem) {
        _insertBeforeItem(item);
      }
    },
    onDragStart: (
      item?: TaskCheckListItem,
      itemIndex?: number,
      selectedItems?: TaskCheckListItem[],
      event?: MouseEvent,
    ) => {
      _setDraggedItem(item);
    },
    onDragEnd: (item?: TaskCheckListItem, event?: DragEvent) => {
      _setDraggedItem(undefined);
    },
  };

  return (
    <IPStack id={rootId.current} verticalFill tokens={globalStackTokensGapSmall}>
      {isPage && items.length > 0 && (
        <IPStack.Item styles={globalStackItemStylesPaddingScene}>
          <IPStack horizontal wrap verticalAlign="center" tokens={globalStackTokensGapMedium}>
            <IPStack.Item>
              <IPSearchBox
                componentRef={searchBoxRef}
                underlined={true}
                styles={globalSearchBoxStyles}
                placeholder={t('translation:General.Filter.Placeholder')}
                value={searchInput}
                onChange={(ev, newValue) => {
                  setSearchInput(newValue ?? '');
                  clearSearchTimer();
                  searchTimerRef.current = setTimeout(() => setSearchText(newValue ?? ''), globalFilterDelay);
                }}
                onClear={() => {
                  clearSearchTimer();
                  setSearchText('');
                }}
              />
            </IPStack.Item>
            <IPStack.Item>
              <IPCheckbox
                label={`${t('task:CheckList.Todo')} (${countByState(TaskCheckListItemState.ToDo)})`}
                checked={showOpen}
                onChange={(ev, checked) => setShowOpen(!!checked)}
              />
            </IPStack.Item>
            <IPStack.Item>
              <IPCheckbox
                label={`${t('task:CheckList.Success')} (${countByState(TaskCheckListItemState.Success)})`}
                checked={showSuccess}
                onChange={(ev, checked) => setShowSuccess(!!checked)}
              />
            </IPStack.Item>
            <IPStack.Item>
              <IPCheckbox
                label={`${t('task:CheckList.Failed')} (${countByState(TaskCheckListItemState.Failed)})`}
                checked={showFailed}
                onChange={(ev, checked) => setShowFailed(!!checked)}
              />
            </IPStack.Item>
            {filterActive && (
              <IPStack.Item>
                <IPText variant="small">
                  {t('translation:General.List.FilterCount', { total: items.length, filtered: visibleItems.length })}
                </IPText>
              </IPStack.Item>
            )}
          </IPStack>
        </IPStack.Item>
      )}
      {items.length === 0 && !props.hideEmptyState && (
        <IPStack grow horizontalAlign="center" verticalAlign="center" tokens={globalStackTokensGapLarge}>
          <IPStack.Item>
            <IPText>{t('task:CheckList.NoData')}</IPText>
          </IPStack.Item>
          {props.allowEdit && !appContext.isMobileView && hasUserFeature(appContext, FeatureTypes.KPIsAndForms) && (
            <IPStack.Item styles={{ root: { width: '50%', minwidth: 200 } }}>
              <IPText variant="small">{t('task:CheckList.UpgradeFormInfo')}</IPText>
              <LearnMore learnMoreLink={globalKB_howToWorkWithFormsChecklist} />
            </IPStack.Item>
          )}
        </IPStack>
      )}
      {items.length > 0 && visibleItems.length === 0 && (
        <IPStack.Item styles={globalStackItemStylesPaddingScene}>
          <IPText variant="small">{t('task:CheckList.NoMatching')}</IPText>
        </IPStack.Item>
      )}
      {visibleItems.length > 0 && (
        <IPStack.Item grow styles={getGlobalStackItemStylesPaddingSceneScrollMinHeight(80)}>
          <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
            <IPDetailsList
              compact
              items={visibleItems}
              columns={columns}
              selectionMode={IPSelectionMode.none}
              isHeaderVisible={false}
              dragDropEvents={props.allowEdit && !filterActive ? dragAndDropEventHandler : undefined}
            />
          </IPScrollablePane>
        </IPStack.Item>
      )}
      {props.allowEdit && (
        <IPStack.Item style={{ paddingLeft: '5px' }}>
          <AddNewCheckListItem
            ref={addItemRef}
            onAddNewCheckListItems={isPage ? addNewCheckListItems : undefined}
            pasteAsList={isPage}
            onPendingChange={props.onPendingNewItemChange}
            onAddNewCheckListItem={onAddNewCheckListItem}
            allowEdit={props.allowEdit ? true : false}
            singleLineEdit={props.singleLineEdit || isPage || appContext.isMobileView}
            compact={props.compact}
          />
        </IPStack.Item>
      )}
    </IPStack>
  );
};

export default CheckList;
